import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
}) {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div className="absolute inset-0 bg-[var(--color-warm-900)]/40 backdrop-blur-sm" />
      <div
        className={`
          relative bg-[var(--color-cream-50)]
          rounded-[var(--radius-xl)]
          shadow-[var(--shadow-xl)]
          w-full ${widths[size]}
          border border-[var(--color-warm-100)]
          animate-slide-up
          max-h-[90vh] flex flex-col
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || description) && (
          <div className="px-6 pt-6 pb-4 border-b border-[var(--color-warm-100)]">
            {title && (
              <h2 className="text-[18px] font-bold text-[var(--color-warm-900)] tracking-tight">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-[13px] text-[var(--color-warm-600)] mt-1">
                {description}
              </p>
            )}
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>

        {footer && (
          <div className="px-6 py-4 border-t border-[var(--color-warm-100)] flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

Modal.Footer = function ModalFooter({ onCancel, onConfirm, confirmLabel = 'Confirm', cancelLabel = 'Cancel', loading, variant = 'primary' }) {
  return (
    <>
      <Button variant="ghost" onClick={onCancel}>{cancelLabel}</Button>
      <Button variant={variant} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
    </>
  );
};
